import { BREAKPOINTS, COLORS, LoadingIndicator, SystemIcon, SystemIcons, TextButton } from "@laerdal/life-react-components";
import { PageWidthFull } from "../../../commonComponents/PageWidthFull";
import { marketList } from "../../../model/constants/marketContants";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import SignIn from "./SignIn";
import { useEffect, useState } from "react";
import { posthog } from "posthog-js";
import { selectIsAuthenticated, signInWithAuth0 } from "../../../store/slices/userSlice";
import { useSelector } from "react-redux";
import { AssetListItemDto } from "../../../model/dto/assetListItemDto";
import SearchApi from "../../../services/api/SearchApi";
import ProductListItem from "../../../commonComponents/ProductListItem";
import ProductCardItem from "../../../commonComponents/ProductCardItem";
import NoAssets from "../../../commonComponents/NoAssets";

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: start;
    gap:32px;
    display: flex;
    align-items: start;
`;

const SectionTitle = styled.div`
    font-family: Lato;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%; /* 28.8px */
    ${BREAKPOINTS.MEDIUM}{
        font-size: 32px;
        line-height: 32px;
    }
`;

const AssetGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;

    ${BREAKPOINTS.LARGE}{
        grid-template-columns: repeat(3, 1fr);
    }
`;

const MobileOnly = styled.div`
    display: block;
    width: 100%;

    ${BREAKPOINTS.LARGE} {
        display: none;
    }
`;

const DesktopOnly = styled.div`
    display: none;
    width: 100%;

    ${BREAKPOINTS.LARGE} {
        display: block;
    }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  ${BREAKPOINTS.LARGE} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ViewAllButtonDesktop = styled.div`
  display: none;

  ${BREAKPOINTS.LARGE} {
    display: block;
  }
`;

const ViewAllButtonMobile = styled.div`
  display: block;

  ${BREAKPOINTS.LARGE} {
    display: none;
  }
`;

interface ProductAssetsProps {
  currentMarket: string;
  categoryLink?: string;
  productLink?: string;
  productLineName?: string;
}

const ProductAssets = ({ currentMarket, categoryLink, productLink, productLineName }: ProductAssetsProps) => {
  const { t } = useTranslation('landingPage');
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [assets, setAssets] = useState<AssetListItemDto[]>([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const posthogData = {
    current_market: currentMarket,
    category_link: categoryLink,
    product_link: productLink
  };

  useEffect(() => {
    if (isAuthenticated && productLineName) {
      setAssetsLoading(true);
      SearchApi.AssetSearch({ productLineName, productLineCode: productLink, pageSize: 3, page: 0 })
        .then((response) => {
          setAssets(response.items ?? []);
          setAssetsTotal(response.filteredCount ?? 0);
        })
        .catch(() => {
          setAssets([]);
          setAssetsTotal(0);
        })
        .finally(() => {
          setAssetsLoading(false);
        });
    }
  }, [isAuthenticated, productLineName]);

  const onSignInSubmit = (emailAddress: string) => {
    posthog?.capture?.('SUPP-ProductLinePage OnboardingSignInSubmitted', posthogData);
    signInWithAuth0(window.location.href || '', undefined, emailAddress);
  };

  return (
    <>
      {!isAuthenticated &&
        <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true} >
          <PageContainer>
            <SectionTitle id='discover-more-about-your-product'>{t('Discover more about your product')}</SectionTitle>
            <SignIn onSignInSubmit={onSignInSubmit} />
          </PageContainer>
        </PageWidthFull>
      }
      {isAuthenticated &&
        <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true} >
          <PageContainer>
            <SectionHeader>
              <SectionTitle id='my-products'>{t('Owned by your organization')}</SectionTitle>
              <ViewAllButtonDesktop>
                {assets.length > 0 ? (
                  <TextButton variant="tertiary" onClick={() => {
                    const connectUrl = process.env.REACT_APP_CONNECT_LINK || '';
                    window.open(`${connectUrl}/products?filter=All&sorting=newest&rows=24&productLineName=${encodeURIComponent(productLineName ?? '')}&productLineCode=${encodeURIComponent(productLink ?? '')}`, '_blank');
                  }}>{t('View all')}{assetsTotal > 3 && ` (+${assetsTotal - 3})`} <SystemIcons.ArrowLineRight size="20px" style={{ verticalAlign: 'middle', padding: '0 0 2px 0' }} /></TextButton>
                ) : (
                  <TextButton variant="tertiary" onClick={() => {
                    const connectUrl = process.env.REACT_APP_CONNECT_LINK || '';
                    window.open(`${connectUrl}/products?filter=All&sorting=newest&rows=24`, '_blank');
                  }}>{t('Go to my products')} <SystemIcons.ArrowLineRight size="20px" style={{ verticalAlign: 'middle', padding: '0 0 2px 0' }} /></TextButton>
                )}
              </ViewAllButtonDesktop>
            </SectionHeader>
            {assetsLoading ? (
              <LoadingIndicator />
            ) : assets.length > 0 ? (
              <>
                <MobileOnly>
                  <AssetGrid>
                    {assets.map((asset, index) => (
                      <ProductListItem
                        key={`list-${index}`}
                        title={asset.name ?? asset.localizedName ?? asset.skuName ?? t('Product')}
                        description={asset.serialNumber ?? undefined}
                        imageUrl={`https://www.laerdal.com/cdn-cgi/image/format=avif,quality=100,height=160,fit=contain/images/sku/${asset.sku}.png?lang=en`}
                        onClick={() => window.open(`${process.env.REACT_APP_CONNECT_LINK || ''}/products/${asset.id}`, '_blank')}
                      />
                    ))}
                  </AssetGrid>
                </MobileOnly>
                <DesktopOnly>
                  <AssetGrid>
                    {assets.map((asset, index) => (
                      <ProductCardItem
                        key={`card-${index}`}
                        title={asset.name ?? asset.localizedName ?? asset.skuName ?? t('Product')}
                        description={asset.serialNumber ?? undefined}
                        imageUrl={`https://www.laerdal.com/cdn-cgi/image/format=avif,quality=100,height=320,fit=contain/images/sku/${asset.sku}.png?lang=en`}
                        onClick={() => window.open(`${process.env.REACT_APP_CONNECT_LINK || ''}/products/${asset.id}`, '_blank')}
                      />
                    ))}
                  </AssetGrid>
                </DesktopOnly>
                <ViewAllButtonMobile>
                  <TextButton variant="tertiary" onClick={() => {
                    const connectUrl = process.env.REACT_APP_CONNECT_LINK || '';
                    window.open(`${connectUrl}/products?filter=All&sorting=newest&rows=24&productLineName=${encodeURIComponent(productLineName ?? '')}&productLineCode=${encodeURIComponent(productLink ?? '')}`, '_blank');
                  }}>{t('View all')}{assetsTotal > 3 && ` (+${assetsTotal - 3})`} <SystemIcons.ArrowLineRight size="20px" style={{ verticalAlign: 'middle', padding: '0 0 2px 0' }} /></TextButton>
                </ViewAllButtonMobile>
              </>
            ) : (
              <NoAssets productLineName={productLineName} />
            )}
          </PageContainer>
        </PageWidthFull>
      }
    </>
  );
};

export default ProductAssets;
